import { difference } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { action, computed, makeObservable, observable } from 'mobx';
import { arrayWithNewItem } from '../../common/array';
import { AcceptItem, Award, AwardGroup, AwardRef, EnrolmentFreshness } from '../../models';

export class SelectAwardPageUiStore {
  @observable awardGroups: AwardGroup[] = [];

  @observable eligibleAwardRefs: AwardRef[] = [];

  @observable isCheckingEligibility: boolean = true;

  @observable enrolmentSyncProgress: number = 0;

  @observable enrolmentFreshness?: EnrolmentFreshness;
  
  @observable selectedAward?: Award;

  @observable acceptItems: AcceptItem[] = [];

  @observable acceptedItemIds: number[] = [];

  @observable isDiscrepancyReportTicketSent: boolean = false;

  @observable progress = 0;

  @observable isRequirementsAccepted: boolean = false;

  @observable isContestRulesAccepted: boolean = false;

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.awardGroups = [];
    this.eligibleAwardRefs = [];
    this.isCheckingEligibility = true;
    this.enrolmentSyncProgress = 0;
    this.enrolmentFreshness = undefined;
    this.selectedAward = undefined;
    this.isDiscrepancyReportTicketSent = false;
    this.acceptItems = [];
    this.acceptedItemIds = [];
    this.progress = 0;
    this.isRequirementsAccepted = false;
    this.isContestRulesAccepted = false;
  }

  @action
  setAwardGroups(awardGroups: AwardGroup[]): void {
    this.awardGroups = awardGroups;
  }

  @action
  setEligibleAwardRefs(eligibleAwardRefs: AwardRef[]): void {
    this.eligibleAwardRefs = eligibleAwardRefs;
  }

  @action
  setIsCheckingEligibility(isCheckingEligibility: boolean): void {
    this.isCheckingEligibility = isCheckingEligibility;
  }

  @action
  setEnrolmentSyncProgress(progress: number): void {
    this.enrolmentSyncProgress = progress;
  }

  @action
  setEnrolmentFreshness(enrolmentFreshness?: EnrolmentFreshness): void {
    this.enrolmentFreshness = enrolmentFreshness;
  }

  @action
  setSelectedAward(award: Award): void {
    this.selectedAward = award;
  }

  @action
  setIsDiscrepancyReportTicketSent(isDiscrepancyReportTicketSent: boolean): void {
    this.isDiscrepancyReportTicketSent = isDiscrepancyReportTicketSent;
  }

  @action
  setAcceptItems(acceptItems: AcceptItem[]): void {
    this.acceptItems = acceptItems;
  }

  @action
  setAcceptedItemIds(acceptItemId: number): void {
    this.acceptedItemIds = arrayWithNewItem(this.acceptedItemIds, acceptItemId);
  }

  @action
  nextProgress(): void {
    if (this.canProgress) {
      this.progress++;
    }
  }

  @action
  setIsRequirementsAccepted(value: boolean): void {
    this.isRequirementsAccepted = value;
  }

  @action
  setIsContestRulesAccepted(value: boolean): void {
    this.isContestRulesAccepted = value;
  }

  @action
  resetAcceptance(): void {
    this.isRequirementsAccepted = false;
    this.isContestRulesAccepted = false;
  }

  @computed
  get isAccepted(): boolean {
    return this.isRequirementsAccepted && this.isContestRulesAccepted;
  }

  @computed
  get sortedAcceptItems() {
    return this.acceptItems.toSorted((item1, item2) => item1.ordinal - item2.ordinal);
  }

  @computed
  get currentAcceptItem() {
    return this.sortedAcceptItems[this.progress];
  }

  @computed
  get canProgress() {
    return this.progress < this.acceptItems.length - 1;
  }

  @computed
  get needToAcceptItems(): boolean {
    if (isEmpty(this.acceptItems)) {
      return false;
    }

    const allItemIds = this.acceptItems.map((item) => item.acceptItemId);
    const unacceptedItemIds = difference(allItemIds, this.acceptedItemIds);

    return !isEmpty(unacceptedItemIds);
  }

  @computed
  get eligibleAwardIdSet(): ReadonlySet<number> {
    return new Set(this.eligibleAwardRefs.map((awardRef) => awardRef.awardId));
  }

  isEligibleAward = (awardId: number): boolean => {
    return this.eligibleAwardIdSet.has(awardId);
  };

  @computed
  get isSelectedAwardNotEligible(): boolean {
    return this.selectedAward ? !this.isEligibleAward(this.selectedAward.awardId) : false;
  }
}
